import type { MissingDefaultChannel } from "./MissingDefaultBannerDialog";

/** Row emitted by POST /BannerSwap/Queue when a saved default fails the YouTube upload check. */
export type InvalidDefaultBannerRow = {
  channelId: string;
  message: string;
};

type QueueErrorLike = {
  response?: {
    status?: number;
    data?: { invalidDefaultBanners?: unknown };
  };
};

function isInvalidDefaultBannerRow(value: unknown): value is InvalidDefaultBannerRow {
  if (!value || typeof value !== "object") {
    return false;
  }
  const row = value as Partial<InvalidDefaultBannerRow>;
  return typeof row.channelId === "string" && row.channelId.trim().length > 0;
}

/**
 * Returns the structured rows when the queue 400 is the saved-default banner check;
 * null for every other failure (overlap 409, validation 400 without rows, network).
 */
export function getInvalidDefaultBannerRows(
  error: unknown
): InvalidDefaultBannerRow[] | null {
  const err = error as QueueErrorLike | null | undefined;
  if (err?.response?.status !== 400) {
    return null;
  }
  const rows = err.response.data?.invalidDefaultBanners;
  if (!Array.isArray(rows)) {
    return null;
  }
  const valid = rows.filter(isInvalidDefaultBannerRow);
  return valid.length > 0 ? valid : null;
}

/** Maps queue rows to dialog entries, using titles the client already loaded. */
export function toMissingDefaultChannels(
  rows: InvalidDefaultBannerRow[],
  channelRows: Array<{ ChannelId?: string; id?: string; ChannelTitle?: string }>
): MissingDefaultChannel[] {
  const titleByChannelId = new Map<string, string | undefined>();
  for (const row of channelRows) {
    const id = row.ChannelId?.trim() || row.id?.trim();
    if (id && !titleByChannelId.has(id)) {
      titleByChannelId.set(id, row.ChannelTitle?.trim() || undefined);
    }
  }
  const seen = new Set<string>();
  const result: MissingDefaultChannel[] = [];
  for (const row of rows) {
    const channelId = row.channelId.trim();
    if (seen.has(channelId)) {
      continue;
    }
    seen.add(channelId);
    result.push({ channelId, channelTitle: titleByChannelId.get(channelId) });
  }
  return result;
}
